import {
  Container,
  Heading,
  BackgroundColorWrapper,
  LeftSide,
  RightSide,
  Subtitle,
  LabelValue,
  LabelKey,
  FormWrapper,
  ContentWrapper,
  EditLabel,
  EditIcon,
  ValueWrapperRelative,
  RowSpaceBetween,
  LoaderWrapper,
  Column,
} from './SimpleWideCard.styled';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/Spinner/Spinner';
import Icon from 'components/Icon/Icon';
import { COLORS } from 'assets/styled';
import {
  excludedDataKeys,
  MAX_LENGTH_DESKTOP,
  MAX_LENGTH_PHONE,
  MAX_LENGTH_TABLET,
} from './constants';
import { PencilSimple } from '@phosphor-icons/react';
import Tooltip from 'components/Tooltip/Tooltip';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import ContactLink from 'components/ContactLink/ContactLink';
import { ContactLinkType } from 'components/ContactLink/contants';

interface ISimpleWideCardProps {
  cardTitle: string;
  leftSubtitle: string | null;
  rightSubtitle: string;
  leftData: object;
  rightData: object | null;
  onEditClick?: () => void;
  showMoreData?: object;
  isLoading?: boolean;
  editable?: boolean;
  pwId?: string;
}
const SimpleWideCard = ({
  cardTitle,
  leftSubtitle,
  rightSubtitle,
  leftData,
  rightData,
  onEditClick,
  isLoading = false,
  editable = false,
  pwId,
}: ISimpleWideCardProps) => {
  const { t } = useTranslation();

  const { isSmallTablet, isPhone } = useBreakpointFlag();
  const maxLength = isPhone
    ? MAX_LENGTH_PHONE
    : isSmallTablet
    ? MAX_LENGTH_TABLET
    : MAX_LENGTH_DESKTOP;

  return (
    <Container>
      <BackgroundColorWrapper>
        <RowSpaceBetween>
          <Heading data-testid={`${pwId}-card-title`}>{cardTitle}</Heading>
          {onEditClick && editable && (
            <ValueWrapperRelative>
              <EditLabel
                data-testid={`${pwId}-edit-label`}
                onClick={onEditClick}
              >
                {t('Edit')}
              </EditLabel>
              <EditIcon>
                <Icon
                  onClick={onEditClick}
                  svg={PencilSimple}
                  color={COLORS.GREEN}
                  size={15}
                />
              </EditIcon>
            </ValueWrapperRelative>
          )}
        </RowSpaceBetween>
        {isLoading ? (
          <LoaderWrapper>
            <Spinner />
          </LoaderWrapper>
        ) : (
          <ContentWrapper>
            <LeftSide>
              {leftSubtitle ? (
                <Subtitle data-testid={`${pwId}-${leftSubtitle}-label`}>
                  {leftSubtitle}
                </Subtitle>
              ) : null}
              <FormWrapper>
                {Object.keys(leftData)
                  .filter((key) => !excludedDataKeys.includes(key))
                  .map((dataKey: string, index: number) => (
                    <Column key={index}>
                      <LabelKey data-testid={`${pwId}-${dataKey}-label`}>
                        {t(dataKey)}
                      </LabelKey>
                      {leftData[dataKey] && (
                        <div>
                          <Tooltip
                            content={
                              dataKey === 'Reference' &&
                              leftData[dataKey]?.length > maxLength
                                ? leftData[dataKey]
                                : undefined
                            }
                          >
                            {dataKey === 'E-mail' ? (
                              <ContactLink
                                value={leftData[dataKey]}
                                type={ContactLinkType.EMAIL}
                                fontSize="14rem"
                              />
                            ) : dataKey === 'Phone number' ? (
                              <ContactLink
                                value={leftData[dataKey]}
                                type={ContactLinkType.PHONE}
                                fontSize="14rem"
                              />
                            ) : (
                              <LabelValue
                                data-testid={`${pwId}-${dataKey}-value`}
                                clickable={
                                  (dataKey === 'Name' &&
                                    leftData['customerID']) ||
                                  (dataKey === 'Company name' &&
                                    leftData['supplierID']) ||
                                  (dataKey === 'Number' &&
                                    !leftData['Name'] &&
                                    leftData['customerID'])
                                }
                              >
                                {dataKey === 'Reference' &&
                                leftData[dataKey]?.length > maxLength
                                  ? `${leftData[dataKey].substring(
                                      0,
                                      maxLength
                                    )}...`
                                  : leftData[dataKey]}
                              </LabelValue>
                            )}
                          </Tooltip>
                        </div>
                      )}
                    </Column>
                  ))}
              </FormWrapper>
            </LeftSide>
            {rightData ? (
              <RightSide>
                {rightSubtitle && (
                  <Subtitle data-testid={`${pwId}-${rightSubtitle}-label`}>
                    {rightSubtitle}
                  </Subtitle>
                )}
                <FormWrapper>
                  {Object.keys(rightData)?.map(
                    (dataKey: string, index: number) => {
                      if (dataKey === 'E-mail') {
                        return (
                          <Column key={index}>
                            <LabelKey>{t(dataKey)}</LabelKey>
                            <div>
                              <ContactLink
                                value={rightData[dataKey]}
                                type={ContactLinkType.EMAIL}
                                fontSize="14rem"
                              />
                            </div>
                          </Column>
                        );
                      } else if (
                        dataKey === 'Phone number' ||
                        dataKey === 'Secondary phone number'
                      ) {
                        return (
                          <Column key={index}>
                            <LabelKey>{t(dataKey)}</LabelKey>
                            <div>
                              <ContactLink
                                value={rightData[dataKey]}
                                type={ContactLinkType.PHONE}
                                fontSize="14rem"
                              />
                            </div>
                          </Column>
                        );
                      }
                      return (
                        <Column key={index}>
                          <LabelKey data-testid={`${pwId}-${dataKey}-label`}>
                            {t(dataKey)}
                          </LabelKey>
                          {rightData[dataKey] !== undefined &&
                            rightData[dataKey] !== null && (
                              <LabelValue
                                data-testid={`${pwId}-${dataKey}-value`}
                              >
                                {rightData[dataKey]}
                              </LabelValue>
                            )}
                        </Column>
                      );
                    }
                  )}
                </FormWrapper>
              </RightSide>
            ) : null}
          </ContentWrapper>
        )}
      </BackgroundColorWrapper>
    </Container>
  );
};
export default SimpleWideCard;
